/**
 * @file
 * Context to append marketing parameters to links outputted by components.
 *
 * The marketing parameters are part of the URL of the host website: they're UTM
 * parameters (e.g. utm_campaign) or other parameters specific to Autodesk's
 * Marketo usage (e.g. __mktvar002).
 */

import { createContext, ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';

export interface MarketingParameter {
  name: string;
  value: string;
}
// The default context value includes a function that simply returns the URL it
// receives as an argument.
// This is useful when the component uses this context is not wrapped in the
// provider, which is the case with AURR-compatible components rendered inside
// the Gutenberg editor. Without this all users of the context would need to
// check whether the context object contains this function.
const MarketingParametersContext = createContext({
  appendToUrl: (url: string) => url,
});

/**
 * Context provider to help with appending marketing parameters to URLs.
 * @param {Object} params Component props.
 * @returns {*} Component providing a context with helpful methods for working
 * with marketing parameters.
 */
function MarketingParametersContextProvider({
  marketingParameters = [],
  children,
}: {
  marketingParameters?: MarketingParameter[];
  children?: ReactNode;
}) {
  /**
   * Returns whether a URL contains a query string parameter or a fragment.
   * @param {string} url URL to check.
   * @returns {boolean} Whether URL contains a query string parameter or a
   * fragment.
   */
  const urlHasQueryStringOrFragment = (url: string) => /\?.*|#.*/.test(url);

  /**
   * Returns a query string from the marketing parameters passed to this
   * component as a prop.
   * @see MarketingParametersContextProvider.propTypes.marketingParameters
   * @returns {string} Query string ready to be appended to a URL (without the ?
   * character at the beginning.)
   */
  const createQueryString = () =>
    marketingParameters.map(p => `${p.name}=${p.value}`).join('&');

  /**
   * Returns URL with the marketing parameters appended if there is any.
   * @param {string} url The URL to which to append marketing parameters.
   * @returns {string} The URL with appended marketing parameters if there is
   * any and the URL doesn't already have a query string.
   */
  const appendToUrl = (url: string) => {
    // Do not append anything to the URL if it has a query string or a fragment.
    if (urlHasQueryStringOrFragment(url)) return url;
    const queryString = createQueryString();
    return queryString ? `${url}?${queryString}` : url;
  };

  return (
    <MarketingParametersContext.Provider
      value={useMemo(() => ({ appendToUrl }), [])}
    >
      {children}
    </MarketingParametersContext.Provider>
  );
}

MarketingParametersContextProvider.propTypes = {
  marketingParameters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
};

MarketingParametersContextProvider.defaultProps = {
  marketingParameters: [],
  children: null,
};

export default MarketingParametersContext;
export { MarketingParametersContextProvider };
